import { FC, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { css, Theme } from '@emotion/react';
import { transparentize } from 'polished';
import type { DeploymentMetricsResponse } from 'api/deployment/handlers/deploymentMetricsHandler';
import { DeploymentMetricType } from 'common/CommonConstants';
import { DeploymentMetricsRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import type { DeploymentMetricsNumericRow } from 'common/deployment/MetricsDocument';
import Api from 'client/lib/Api';
import { expandDataToFillSlices } from 'client/components/Spectrum/spectrumUtils';
import { Statistic } from 'client/components/Statistic';
import { Typography } from 'client/components/Typography';
import { LineGraph } from './LineGraph';

const loadData = async (deploymentID: number, period: string) => {
  const response = await Api.post<DeploymentMetricsRequest, DeploymentMetricsResponse>('/deployment/metrics', {
    deploymentID: deploymentID!,
    type: DeploymentMetricType.CPU,
    resolutionMinutes: period == '1m' ? 1 : period == '1h' ? 60 : 24 * 60,
  });

  // TUOMAS!!! Here is request for memory, works same as cpu but also returns totalMemory, see if you could do something with it
  /*const response2 = await Api.post<DeploymentMetricsRequest, DeploymentMetricsResponse>('/deployment/metrics', {
    deploymentID: deploymentID!,
    type: DeploymentMetricType.MEMORY,
    resolutionMinutes: period == '1m' ? 1 : period == '1h' ? 60 : 24 * 60,
  });
  console.log('RESPONSE2', response2);
  */

  if (response.status !== 'ok') {
    throw new Error('Failed to load CPU metrics');
  }

  const data = (response.data as DeploymentMetricsNumericRow[]).map((d) => ({ time: new Date(d.time), value: d.value * 100 })).slice(-10);
  return data;
};

export const StatisticGraph: FC<{ deploymentID?: number }> = ({ deploymentID, ...other }) => {
  const [period, setPeriod] = useState<'1m' | '1h' | '1d'>('1m');

  const query = useQuery({
    // Cache query based on deploymentID & period
    queryKey: ['metrics.cpu', deploymentID, period],
    queryFn: () => loadData(deploymentID!, period),
    enabled: !!deploymentID,
    retryDelay: 10000,

    // Refresh after 10 seconds
    refetchInterval: 10000,
  });

  const items = useMemo(() => expandDataToFillSlices(query.data || [], 10, period, 'numeric'), [query.data, period]);
  const current = query.data?.at(-1)?.value || 0;

  return (
    <div css={containerStyle} {...other}>
      <Statistic title="CPU" amount={Math.round(current)} unit="%" />
      <Typography variant="body2" component="label" className="period-select">
        {(['1m', '1h', '1d'] as const).map((value, index) => (
          <span key={index} onClick={() => setPeriod(value)} className={period === value ? 'selected' : ''}>
            {value}
          </span>
        ))}
      </Typography>
      <LineGraph data={items.map((item) => ({ ...item, timestamp: new Date(item.time) }))} scale={[0, 100]} />
    </div>
  );
};

const containerStyle = (theme: Theme) => css`
  position: relative;
  /* padding: 26px;
  border-radius: ${theme.borderRadius}px;
  border: 2px solid ${theme.color.grey[60]};
  background: linear-gradient(
    -5deg,
    ${theme.color.background},
    ${transparentize(theme.type === 'light' ? 0.35 : 0, theme.color.primary[60])} 90%
  );
  box-sizing: border-box;
  overflow: hidden; */

  > div:first-of-type {
    margin-bottom: 0.5rem;
  }

  .period-select {
    position: absolute;
    top: 0;
    right: 0.3rem;
    height: 6px;
    font-size: 1rem;
    font-weight: 600;
    transform: translate(6px, 7px) scale(0.75);
    color: ${theme.color.grey[80]};

    > span {
      padding: 0px 5px;
      cursor: pointer;

      &.selected {
        font-weight: bold;
        color: ${theme.color.grey[100]};
      }
    }
  }
`;
